// Programmatic control API for demo mode
// Exposed as window.flowstockDemo for AI agent control

import { getData, setData, resetData as resetStoreData } from './demoData';
import { isDemoMode, deactivateDemo } from './demoMode';

export interface FlowstockDemoAPI {
  // Navigation
  navigate(path: string): void;
  getCurrentPath(): string;

  // DOM interaction
  click(selector: string): Promise<void>;
  type(selector: string, text: string, opts?: { clear?: boolean; delay?: number }): Promise<void>;
  hover(selector: string): Promise<void>;
  scrollTo(selector: string): void;
  focus(selector: string): void;

  // Wait helpers
  wait(ms: number): Promise<void>;
  waitFor(selector: string, timeout?: number): Promise<Element>;

  // Data control
  setData(table: string, data: any[]): void;
  getData(table: string): any[];
  resetData(): void;

  // Visual helpers
  highlight(selector: string, duration?: number): Promise<void>;
  showToast(type: string, message: string): void;

  // Recording step markers
  step(name: string): void;
  getSteps(): Array<{ name: string; timestamp: number }>;
  clearSteps(): void;

  // Scenarios
  runScenario(name: string): Promise<void>;
  listScenarios(): string[];

  // Lifecycle
  isActive(): boolean;
  deactivate(): void;
}

// Global type declaration
declare global {
  interface Window {
    flowstockDemo?: FlowstockDemoAPI;
  }
}

export class DemoController implements FlowstockDemoAPI {
  private _navigate: ((path: string) => void) | null = null;
  private _showToast: ((type: string, message: string) => void) | null = null;
  private _steps: Array<{ name: string; timestamp: number }> = [];
  private _scenarios: Record<string, (api: FlowstockDemoAPI) => Promise<void>> = {};

  // Called by DemoProvider to inject React Router navigate
  setNavigate(fn: (path: string) => void) {
    this._navigate = fn;
  }

  // Called by DemoProvider to inject toast function
  setShowToast(fn: (type: string, message: string) => void) {
    this._showToast = fn;
  }

  // Called to register scenario functions
  registerScenarios(scenarios: Record<string, (api: FlowstockDemoAPI) => Promise<void>>) {
    this._scenarios = { ...this._scenarios, ...scenarios };
  }

  // ── Navigation ────────────────────────────────────────────────

  navigate(path: string): void {
    if (this._navigate) {
      this._navigate(path);
    } else {
      console.warn('[flowstockDemo] navigate not available yet');
    }
  }

  getCurrentPath(): string {
    return window.location.pathname;
  }

  // ── DOM interaction ───────────────────────────────────────────

  async click(selector: string): Promise<void> {
    const el = await this.waitFor(selector);
    el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
    el.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
    el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  async type(selector: string, text: string, opts?: { clear?: boolean; delay?: number }): Promise<void> {
    const el = await this.waitFor(selector) as HTMLInputElement | HTMLTextAreaElement;
    el.focus();

    if (opts?.clear) {
      // Use React-compatible value setting
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype, 'value'
      )?.set || Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype, 'value'
      )?.set;

      if (nativeSetter) {
        nativeSetter.call(el, '');
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const delay = opts?.delay ?? 50;

    for (const char of text) {
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype, 'value'
      )?.set || Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype, 'value'
      )?.set;

      if (nativeSetter) {
        nativeSetter.call(el, el.value + char);
      }

      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));

      if (delay > 0) {
        await this.wait(delay);
      }
    }
  }

  async hover(selector: string): Promise<void> {
    const el = await this.waitFor(selector);
    el.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
    el.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
  }

  scrollTo(selector: string): void {
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  focus(selector: string): void {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) el.focus();
  }

  // ── Wait helpers ──────────────────────────────────────────────

  wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  waitFor(selector: string, timeout = 5000): Promise<Element> {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        const el = document.querySelector(selector);
        if (el) {
          resolve(el);
        } else if (Date.now() - start > timeout) {
          reject(new Error(`[flowstockDemo] waitFor("${selector}") timed out after ${timeout}ms`));
        } else {
          requestAnimationFrame(check);
        }
      };
      check();
    });
  }

  // ── Data control ──────────────────────────────────────────────

  setData(table: string, data: any[]): void {
    setData(table, data);
  }

  getData(table: string): any[] {
    return getData(table);
  }

  resetData(): void {
    resetStoreData();
  }

  // ── Visual helpers ────────────────────────────────────────────

  async highlight(selector: string, duration = 2000): Promise<void> {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: ${rect.top - 4}px;
      left: ${rect.left - 4}px;
      width: ${rect.width + 8}px;
      height: ${rect.height + 8}px;
      border: 3px solid #f97316;
      border-radius: 8px;
      pointer-events: none;
      z-index: 99999;
      animation: flowstock-demo-pulse 0.6s ease-in-out infinite alternate;
    `;

    // Add keyframes if not already present
    if (!document.getElementById('flowstock-demo-styles')) {
      const style = document.createElement('style');
      style.id = 'flowstock-demo-styles';
      style.textContent = `
        @keyframes flowstock-demo-pulse {
          from { opacity: 0.5; transform: scale(1); }
          to { opacity: 1; transform: scale(1.02); }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(overlay);
    await this.wait(duration);
    overlay.remove();
  }

  showToast(type: string, message: string): void {
    if (this._showToast) {
      this._showToast(type, message);
    } else {
      // Fallback: dispatch custom event for DemoProvider to pick up
      window.dispatchEvent(new CustomEvent('flowstock-demo-toast', {
        detail: { type, message },
      }));
    }
  }

  // ── Recording step markers ────────────────────────────────────

  step(name: string): void {
    this._steps.push({ name, timestamp: Date.now() });
    console.log(`[flowstockDemo] Step: ${name}`);
  }

  getSteps(): Array<{ name: string; timestamp: number }> {
    return [...this._steps];
  }

  clearSteps(): void {
    this._steps = [];
  }

  // ── Scenarios ─────────────────────────────────────────────────

  async runScenario(name: string): Promise<void> {
    const scenario = this._scenarios[name];
    if (!scenario) {
      throw new Error(`[flowstockDemo] Unknown scenario "${name}". Available: ${this.listScenarios().join(', ')}`);
    }
    this.clearSteps();
    this.step(`Scenario started: ${name}`);
    await scenario(this);
    this.step(`Scenario completed: ${name}`);
  }

  listScenarios(): string[] {
    return Object.keys(this._scenarios);
  }

  // ── Lifecycle ─────────────────────────────────────────────────

  isActive(): boolean {
    return isDemoMode();
  }

  deactivate(): void {
    deactivateDemo();
  }
}
